import { useCallback } from 'react';

import * as Sentry from '@sentry/react';

import { getPaymentErrorContent, useAfterPurchaseModalStore } from '@/entities/afterPurchase';
import { usePendingPaymentSession } from '@/entities/paymentSession';
import { RemovedBoostersList, usePurchaseConfirmationModalStore } from '@/entities/purchaseConfirmation';

import { API } from '@/shared/api';
import { IBoostersInvoicePreviewResponse } from '@/shared/api/billing/types';
import { useInterfaceLanguage } from '@/shared/i18n';
import { BoosterType } from '@/shared/types/Billing';

import { AllBoosters } from '../lib/types';
import { useBoosters } from './useBoosters';

type BoosterSelection = {
  [BoosterType.SRT]?: boolean;
  [BoosterType.TeamspaceSeat]?: boolean;
  [BoosterType.TranslationPrompting]?: boolean;
};

type UseBoosterConfirmationOptions = {
  seatsCount: number;
  selectedBoosters?: BoosterSelection;
  onCancel?: () => void;
};

type SelectedBoostersMap = Record<string, { isSelected: boolean; quantity: number }>;

const buildBoostersPayload = (allBoosters: AllBoosters | null, selectedBoosters: SelectedBoostersMap) => {
  if (!allBoosters) return [];

  return Object.entries(selectedBoosters)
    .filter(([, data]) => data.isSelected)
    .map(([type, data]) => {
      const booster = allBoosters[type as BoosterType];
      const priceId = booster?.prices[0]?.id;

      if (!priceId) return null;

      return {
        price_id: priceId,
        quantity: data.quantity,
      };
    })
    .filter((item): item is { price_id: string; quantity: number } => item !== null);
};

export const useBoosterConfirmation = () => {
  const { t } = useInterfaceLanguage();
  const openConfirmation = usePurchaseConfirmationModalStore((state) => state.open);
  const closeConfirmation = usePurchaseConfirmationModalStore((state) => state.close);
  const { allBoosters, purchasedBoosters } = useBoosters();

  const { openError } = useAfterPurchaseModalStore();
  const { resolvePaymentResult, showPendingInvoiceIfAny } = usePendingPaymentSession({
    onClosedAfterProcessing: closeConfirmation,
  });

  const openBoosterConfirmation = useCallback(
    ({ seatsCount, selectedBoosters, onCancel }: UseBoosterConfirmationOptions) => {
      const boostersMap: SelectedBoostersMap = {
        [BoosterType.SRT]: {
          isSelected: selectedBoosters?.[BoosterType.SRT] ?? !!purchasedBoosters?.[BoosterType.SRT],
          quantity: 1,
        },
        [BoosterType.TeamspaceSeat]: {
          isSelected: selectedBoosters?.[BoosterType.TeamspaceSeat] ?? !!purchasedBoosters?.[BoosterType.TeamspaceSeat],
          quantity: seatsCount,
        },
        [BoosterType.TranslationPrompting]: {
          isSelected:
            selectedBoosters?.[BoosterType.TranslationPrompting] ??
            !!purchasedBoosters?.[BoosterType.TranslationPrompting],
          quantity: 1,
        },
      };

      void (async () => {
        if (await showPendingInvoiceIfAny()) return;

        openConfirmation({
          promoCodeConfig: null,
          renderExtra: () => (
            <RemovedBoostersList
              allBoosters={allBoosters}
              purchasedBoosters={purchasedBoosters}
              selectedBoosters={boostersMap}
            />
          ),
          fetchPreview: () => {
            const payload = buildBoostersPayload(allBoosters, boostersMap);
            return API.getBoostersInvoicePreview({
              boosters: payload,
            });
          },
          onCancel,
          onConfirm: async (invoice: IBoostersInvoicePreviewResponse) => {
            if (await showPendingInvoiceIfAny()) return;
            const payload = buildBoostersPayload(allBoosters, boostersMap);

            try {
              const res = await API.purchaseBoosters({
                boosters: payload,
              });

              const amountDueToday = invoice?.instant_payment?.amount ?? 0;
              const description = amountDueToday === 0 ? t('boosters.success.saved') : t('boosters.success.changed');

              await resolvePaymentResult({
                response: res,
                showInvoiceLink: !!amountDueToday,
                successDescription: description,
              });
            } catch (error) {
              Sentry.captureException(error);
              const errorContent = getPaymentErrorContent(undefined, t);
              openError(errorContent.message, '', errorContent.buttonText);
              throw error;
            }
          },
        });
      })();
    },
    [allBoosters, openConfirmation, openError, purchasedBoosters, resolvePaymentResult, showPendingInvoiceIfAny, t],
  );

  return { openBoosterConfirmation };
};
